import { cn } from '@/lib/classMerge'
import { cva, VariantProps } from 'class-variance-authority'
import { ComponentProps } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TooltipArrow
} from '@/components/ui/tooltip'
import CircularLoading from './CircularLoading'
import { LucideIcon } from 'lucide-react'

const buttonStyles = cva(['inline-flex items-center whitespace-nowrap'], {
  variants: {
    variant: {
      default: ['bg-secondary', 'hover:bg-secondary-hover'],
      ghost: ['bg-white text-gray-40 text-subtitle font-inter capitalize transition ease-in-out hover:bg-gray-800 hover:text-gray-40 active:bg-gray-500 active:text-gray-40 focus:bg-gray-900 focus:text-gray-40 disabled:bg-gray-900 disabled:text-gray-800 disabled:cursor-not-allowed flex justify-center items-center disabled:cursor-not-allowed'],
      cta: ['font-medium', 'text-ui-darkGray', 'bg-ui-lightGray'],
      black: [
        'bg-ui-black',
        'text-white',
        'font-semibold',
        'border',
        'border-ui-black',
        'hover:bg-white',
        'duration-200',
        'ease-in-out',
        'hover:text-ui-black'
      ],
      plain: [
        'text-center text-base text-neutral-700 font-medium border border-gray-200 transition ease-in-out hover:border-ui-orange hover:text-ui-orange disabled:opacity-50 disabled:cursor-not-allowed'
      ],
      plainBlue: [
        'text-center text-base text-white font-medium bg-ui-orange border border-ui-orange transition ease-in-out hover:bg-transparent hover:text-ui-orange disabled:opacity-50 disabled:cursor-not-allowed'
      ],
      plainOrangeOutlined: [
        'text-center text-base text-neutral-700 font-medium border border-gray-200 transition ease-in-out hover:border-ui-orange hover:text-ui-orange disabled:opacity-50 disabled:cursor-not-allowed'
      ],
      plainOrange: [
        'text-center text-base text-white font-medium bg-ui-orange border border-ui-orange transition ease-in-out hover:bg-transparent hover:text-ui-orange disabled:opacity-50 disabled:cursor-not-allowed hover:border-ui-orange'
      ],
      plainBlueOpposite: [
        'text-center text-base text-ui-plainBlue font-medium bg-transparent border border-ui-plainBlue transition ease-in-out hover:bg-ui-plainBlue hover:text-white disabled:opacity-50 disabled:cursor-not-allowed'
      ],
      danger: [
        'text-center text-base text-ui-cinnabar font-medium bg-transparent border border-ui-cinnabar transition ease-in-out hover:bg-ui-cinnabar hover:text-white disabled:opacity-50 disabled:cursor-not-allowed'
      ],
      oppositeDanger: [
        'text-center text-base text-white font-medium bg-ui-cinnabar border border-ui-cinnabar transition ease-in-out hover:bg-white hover:text-ui-cinnabar disabled:opacity-50 disabled:cursor-not-allowed'
      ],
      primary: [
        'border-primary-300 focus:border-primary focus:ring-1 focus:ring-primary bg-primary-500 text-white border rounded-[40px]'
      ],
      orange: [
        'bg-primary-500 text-white font-inter font-semibold text-center capitalize transition ease-in-out hover:bg-primary-400 focus:ring-2 focus:ring-primary-700 active:bg-primary-300 rounded-[28px] disabled:bg-primary-300 disabled:border-transparent disabled:cursor-not-allowed'
      ],
      deletion: [
        'bg-red-500 text-white font-inter font-semibold text-center capitalize transition ease-in-out hover:bg-red-400 focus:ring-2 focus:ring-primary-700 active:bg-red-300 rounded-[28px] disabled:bg-red-300 disabled:border-transparent disabled:cursor-not-allowed'
      ],
      outline: [
        'bg-white text-gray-40 text-subtitle font-inter capitalize border border-gray-100 transition ease-in-out hover:bg-gray-800 hover:border-gray-800 hover:text-gray-40 active:border-gray-800 active:bg-gray-500 active:text-gray-40 focus:border-orange-700 focus:bg-gray-900 focus:text-gray-40 disabled:bg-gray-900 disabled:text-gray-800 disabled:border-transparent disabled:cursor-not-allowed flex justify-center items-center disabled:cursor-not-allowed'
      ],
      secondary: [
        'border-[#EDEDED]',
        'focus:border-[#EDEDED]',
        'focus:ring-1',
        'focus:ring-[#EDEDED]',
        'bg-[#ffffff]',
        'text-gray-500',
        'border',
        'rounded-[40px]'
      ],
      back: [
        'text-gray-500',
        'hover:text-gray-700',
        'transition-colors',
        'bg-transparent',
        'border-none',
        'p-0'
      ],
      close: [
        'bg-gray-20 text-gray-500 border-none transition-colors ease-in-out',
        'hover:bg-gray-50 hover:text-gray-600',
        'focus:bg-gray-100 focus:text-gray-700 focus:outline-none',
        'active:bg-gray-100 active:text-gray-700',
        'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed'
      ],

      clear: ['']
    },
    size: {
      lg: ['px-6 py-3 text-base', '[&>svg+span]:ml-8'],
      md: ['px-5 py-2 text-sm', '[&>svg+span]:ml-4'],
      sm: ['px-4 py-1.5 text-sm', '[&>svg+span]:ml-4'],
      default: ['rounded', 'p-2'],
      icon: [
        'rounded-full',
        'w-10',
        'h-10',
        'flex',
        'items-center',
        'justify-center',
        'p-2.5'
      ],
      cta: ['px-[25.5px]', 'py-[14px]', 'rounded-[32px]'],
      black: ['px-6', 'py-4', 'rounded-lg'],
      plain: ['py-3 px-5 rounded-[40px]'],
      primary: ['px-6', 'py-4'],
      orange: ['py-3', 'px-6', 'text-base'],
      outline: ['px-4', 'py-1.5', 'rounded-[28px]', 'min-w-20', 'text-[13px]', 'leading-[20px]', 'font-semibold'],
      secondary: ['px-6', 'py-4', 'rounded-[40px]'],
      close: ['p-1', 'rounded-[28px]', 'w-8', 'h-8', 'flex', 'items-center', 'justify-center'],
      clear: ['']
    },
    showLabel: {
      true: '',
      false: '[&>span]:hidden'
    },
    hasIcon: {
      true: 'gap-2',
      false: 'justify-center'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
    showLabel: true,
    hasIcon: false
  }
})

type ButtonProps = VariantProps<typeof buttonStyles> &
  ComponentProps<'button'> & {
    tooltip?: string
    tooltipSide?: 'top' | 'right' | 'bottom' | 'left'
    tooltipAlign?: 'start' | 'center' | 'end'
    loading?: boolean
    icon?: LucideIcon
    showLabel?: boolean
  }

export default function Button({
  variant,
  size,
  className,
  tooltip,
  tooltipSide = 'top',
  tooltipAlign = 'center',
  loading = false,
  children,
  disabled,
  icon: Icon,
  showLabel = true,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading
  const hasIcon = Boolean(Icon || loading)

  const buttonContent = loading ? (
    <>
      <CircularLoading className="flex-shrink-0" />
    </>
  ) : (
    <>
      {Icon && <Icon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" aria-hidden="true" />}
      {children}
    </>
  )

  const button = (
    <button
      {...props}
      disabled={isDisabled}
      className={cn(
        buttonStyles({ variant, size, showLabel, hasIcon }),
        className,
        isDisabled && 'cursor-not-allowed opacity-50 hover:opacity-50'
      )}
    >
      {buttonContent}
    </button>
  )

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent
            side={tooltipSide}
            align={tooltipAlign}
            sideOffset={5}
            className='z-50 overflow-hidden rounded-md bg-[#1C1C1C] px-3 py-1.5 text-sm text-white shadow-md transition-all duration-200 ease-in-out animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
          >
            {tooltip}
            <TooltipArrow className='fill-[#1C1C1C] transition-all duration-200 ease-in-out' />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return button
}